import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { AtSign, Bold, Code, EyeOff, File as FileIcon, Hash, Heading, Italic, List, ListChecks, Paperclip, Quote, SendHorizontal, Smile, Strikethrough, Timer, Type, X } from 'lucide-react';
import { LIMITS, Permission, SLOWMODE_BYPASS, type Attachment, type InteractionState, type PublicUser, permHasAny } from '@spawnwire/shared';
import { Avatar, clsx, IconButton, UserBadge } from '../../components/ui';
import { errorMessage, http, uploadFile } from '../../lib/api';
import { loadMembers, sendMessage } from '../../lib/data';
import { replaceShortcodes, searchEmoji } from '../../lib/emoji';
import { clearTypingThrottle, sendTyping } from '../../lib/gateway';
import { drafts } from '../../lib/local';
import { encodeMentions, mentionableUsers } from '../../lib/mentionText';
import { can, useChannelPermissions } from '../../lib/perms';
import { useNow } from '../../lib/hooks';
import { formatBytes, relativeFuture } from '../../lib/time';
import { displayNameIn, privateChannelName, useStore } from '../../store/store';
import { ui, useUi } from '../../store/ui';
import { EmojiPicker } from './EmojiPicker';
import { InteractionNotices } from './InteractionNotices';
import { commandPrefix, commandStep, commandValues, loadCommands, pickCommand, requestSuggestions, upsertInteraction, useCommands } from './slashCommands';

interface UploadItem {
  key: string;
  file: File;
  progress: number;
  attachment: Attachment | null;
  error: string | null;
  abort: () => void;
  preview: string | null;
}

interface Suggestion {
  key: string;
  label: string;
  detail?: string;
  insert: string;
  icon?: 'user' | 'role' | 'channel' | 'emoji' | 'everyone' | 'command' | 'choice';
  userId?: string;
  emoji?: string;
  color?: string | null;
  /** Slash commands: which one, and the bot that answers it. */
  commandId?: string;
  bot?: PublicUser;
}

/** When slow mode lets you post again, per channel (kept across channel switches). */
const cooldowns = new Map<string, number>();

let serverConfig: { maxUploadBytes: number } | null = null;
async function uploadLimit(): Promise<number> {
  serverConfig ??= await http.get<{ maxUploadBytes: number }>('/config').catch(() => ({ maxUploadBytes: 25 * 1024 * 1024 }));
  return serverConfig.maxUploadBytes;
}

export interface ComposerHandle {
  addFiles: (files: File[]) => void;
  focus: () => void;
}

export const Composer = forwardRef<ComposerHandle, { channelId: string; placeholder?: string }>(function Composer({ channelId, placeholder }, ref) {
  const channel = useStore((s) => s.channels[channelId]);
  const perms = useChannelPermissions(channelId);
  const myMember = useStore((s) => (channel?.spaceId ? s.myMembers[channel.spaceId] : undefined));
  const replyToId = useUi((s) => s.replyTo[channelId]);
  const replyMessage = useStore((s) => (replyToId ? s.messages[channelId]?.byId[replyToId] : undefined));
  const [text, setText] = useState(() => drafts.get(channelId));
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [showFormatting, setShowFormatting] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selected, setSelected] = useState(0);
  const [trigger, setTrigger] = useState<{ start: number; end: number } | null>(null);
  /** The slash command picked from the list (several bots may have one with the same name). */
  const [activeCommandId, setActiveCommandId] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const isThread = channel?.type === 'thread';
  const sendBit = isThread ? Permission.SEND_MESSAGES_IN_THREADS : Permission.SEND_MESSAGES;
  const timedOut = myMember?.timeoutUntil && new Date(myMember.timeoutUntil).getTime() > Date.now();
  // Spawnwire's system account only sends notices; its conversation is read-only.
  const systemDm = channel?.type === 'dm' && !!channel.recipients?.some((u) => u.system);
  const canSend = can(perms, sendBit) && !channel?.archived && !systemDm;
  const canAttach = canSend && can(perms, Permission.ATTACH_FILES);
  const spaceId = channel?.spaceId ?? null;
  // Slow mode: threads follow the channel they are in; people who manage messages or channels skip it.
  const parentChannel = useStore((s) => (channel?.type === 'thread' && channel.parentId ? s.channels[channel.parentId] : undefined));
  const slowmode = (channel?.type === 'thread' ? parentChannel?.slowmodeSeconds : channel?.slowmodeSeconds) ?? 0;
  const slowmodeOn = !!spaceId && slowmode > 0 && !permHasAny(perms, SLOWMODE_BYPASS);
  const [cooldownUntil, setCooldownUntil] = useState(() => cooldowns.get(channelId) ?? 0);
  const now = useNow(cooldownUntil > Date.now() ? 1000 : 60_000);
  const cooling = slowmodeOn && cooldownUntil > now;
  const secondsLeft = Math.max(1, Math.ceil((cooldownUntil - now) / 1000));

  useEffect(() => {
    if (spaceId) void loadMembers(spaceId).catch(() => {});
  }, [spaceId]);

  // Slash commands: the space's commands are fetched when a space is opened (its menus offer the bots' user and
  // message commands), and again whenever a bot changes them.
  const commands = useCommands((s) => (spaceId ? s.bySpace[spaceId] : undefined));
  useEffect(() => {
    if (spaceId) void loadCommands(spaceId);
  }, [spaceId]);
  // Only typed commands are offered after a slash; user and message commands live in menus.
  const chatCommands = useMemo(() => commands?.filter((c) => c.type === 'chat'), [commands]);
  const typed = spaceId ? commandPrefix(text) : null;
  const sameName = typed && chatCommands ? chatCommands.filter((c) => c.name === typed.name) : [];
  const command = typed && chatCommands ? pickCommand(chatCommands, typed.name, activeCommandId) : undefined;
  const step = command && typed?.args != null ? commandStep(command, typed.args) : null;
  const currentOption = step?.current ?? null;

  useEffect(() => {
    const t = setTimeout(() => drafts.set(channelId, text), 300);
    return () => clearTimeout(t);
  }, [text, channelId]);

  const uploadsRef = useRef(uploads);
  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);
  useEffect(() => () => uploadsRef.current.forEach((u) => u.preview && URL.revokeObjectURL(u.preview)), []);

  // Resize the textarea to its content (up to a cap).
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [text]);

  const addFiles = useCallback(
    async (files: File[]) => {
      if (!canAttach) {
        ui.toast('You cannot attach files in this channel.', 'error');
        return;
      }
      const limit = await uploadLimit();
      const room = LIMITS.attachmentsPerMessage - uploads.length;
      if (files.length > room) ui.toast(`You can attach up to ${LIMITS.attachmentsPerMessage} files per message.`, 'error');
      for (const file of files.slice(0, Math.max(0, room))) {
        if (file.size > limit) {
          ui.toast(`${file.name} is larger than the ${formatBytes(limit)} limit.`, 'error');
          continue;
        }
        const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
        const handle = uploadFile<Attachment>(`/channels/${channelId}/attachments`, file, (p) =>
          setUploads((list) => list.map((u) => (u.key === key ? { ...u, progress: p } : u))),
        );
        const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
        setUploads((list) => [...list, { key, file, progress: 0, attachment: null, error: null, abort: handle.abort, preview }]);
        handle.promise
          .then((attachment) => setUploads((list) => list.map((u) => (u.key === key ? { ...u, attachment, progress: 1 } : u))))
          .catch((err) => setUploads((list) => list.map((u) => (u.key === key ? { ...u, error: errorMessage(err) } : u))));
      }
    },
    [canAttach, channelId, uploads.length],
  );

  useImperativeHandle(ref, () => ({ addFiles: (files) => void addFiles(files), focus: () => area.current?.focus() }), [addFiles]);

  function removeUpload(u: UploadItem) {
    if (!u.attachment && !u.error) u.abort();
    if (u.attachment) void http.del(`/attachments/${u.attachment.id}`).catch(() => {});
    if (u.preview) URL.revokeObjectURL(u.preview);
    setUploads((list) => list.filter((x) => x.key !== u.key));
  }

  const uploading = uploads.some((u) => !u.attachment && !u.error);
  const ready = uploads.filter((u) => u.attachment);

  /** Runs the typed slash command; the bot's answer (or "thinking") shows above the composer. */
  async function runCommand(raw: string) {
    if (!command) return;
    const encoded = encodeMentions(replaceShortcodes(raw), useStore.getState(), channelId);
    const result = commandValues(command, encoded.replace(/^\/[a-z0-9_-]+\s*/, ''));
    if ('error' in result) {
      ui.toast(result.error, 'error');
      return;
    }
    setText('');
    drafts.set(channelId, '');
    setSuggestions([]);
    setActiveCommandId(null);
    try {
      upsertInteraction(await http.post<InteractionState>(`/channels/${channelId}/interactions`, { commandId: command.id, subcommand: result.path, options: result.values }));
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
      setText(raw);
    }
  }

  async function submit() {
    const raw = text.trim();
    if (typed && command && ready.length === 0 && !uploading) return runCommand(raw);
    if (typed && sameName.length > 1 && !command) {
      ui.toast(`Several bots have /${typed.name}. Pick the one you mean from the list.`, 'error');
      return;
    }
    if ((!raw && ready.length === 0) || uploading || !canSend || cooling) return;
    if (raw.length > LIMITS.messageLength) {
      ui.toast(`Messages can have at most ${LIMITS.messageLength} characters.`, 'error');
      return;
    }
    const content = encodeMentions(replaceShortcodes(raw), useStore.getState(), channelId);
    const attachments = ready.map((u) => u.attachment!);
    setText('');
    drafts.set(channelId, '');
    uploads.forEach((u) => u.preview && URL.revokeObjectURL(u.preview));
    setUploads([]);
    ui.setReply(channelId, undefined);
    clearTypingThrottle(channelId);
    setSuggestions([]);
    if (slowmodeOn) {
      const until = Date.now() + slowmode * 1000;
      cooldowns.set(channelId, until);
      setCooldownUntil(until);
    }
    await sendMessage(channelId, content, { replyToId, attachments });
  }

  // ---- Autocomplete --------------------------------------------------------------

  const computeSuggestions = useCallback(
    (value: string, caret: number) => {
      const before = value.slice(0, caret);
      // A slash command's name, typed at the very start.
      const slashName = spaceId ? /^\/([a-z0-9_-]*)$/.exec(before.toLowerCase()) : null;
      if (slashName && chatCommands) {
        const q = slashName[1]!;
        const out = chatCommands
          .filter((c) => c.name.startsWith(q))
          .slice(0, 10)
          .map<Suggestion>((c) => ({ key: `cmd${c.id}`, label: `/${c.name}`, detail: `${c.description} · ${c.bot.displayName}`, insert: `/${c.name} `, icon: 'command', commandId: c.id, bot: c.bot }));
        setSuggestions(out);
        setSelected(0);
        setTrigger(out.length ? { start: 0, end: caret } : null);
        return;
      }
      // The choices of the option being typed, if it has any.
      const typedNow = spaceId ? commandPrefix(value) : null;
      const commandNow = typedNow && chatCommands ? pickCommand(chatCommands, typedNow.name, activeCommandId) : undefined;
      const stepNow = commandNow && typedNow?.args != null ? commandStep(commandNow, typedNow.args) : null;
      // Choosing between a command's subcommands (or groups): suggest the names that fit what is typed.
      if (stepNow?.next && caret === value.length) {
        const q = stepNow.partial.toLowerCase();
        const out = stepNow.next
          .filter((n) => n.name.startsWith(q))
          .map<Suggestion>((n) => ({ key: `sub${n.name}`, label: n.name, detail: `${n.type === 'subcommand_group' ? 'Group · ' : ''}${n.description}`, insert: `${n.name} `, icon: 'choice' }));
        setSuggestions(out);
        setSelected(0);
        setTrigger(out.length ? { start: caret - q.length, end: caret } : null);
        return;
      }
      const option = stepNow?.current ?? null;
      if (option?.choices && caret === value.length) {
        const token = /(?:^|\s)(?:[a-z0-9_-]+:)?([^\s]*)$/.exec(before);
        const q = (token?.[1] ?? '').toLowerCase();
        const out = option.choices
          .filter((ch) => String(ch.value).toLowerCase().startsWith(q) || ch.name.toLowerCase().startsWith(q))
          .map<Suggestion>((ch) => ({ key: `choice${String(ch.value)}`, label: ch.name, detail: String(ch.value), insert: `${String(ch.value)} `, icon: 'choice' }));
        if (out.length) {
          setSuggestions(out);
          setSelected(0);
          setTrigger({ start: caret - q.length, end: caret });
          return;
        }
      }
      const m = /(^|[\s(])([@#:])([\p{L}\p{N}_.-]*)$/u.exec(before);
      if (!m) {
        setSuggestions([]);
        setTrigger(null);
        return;
      }
      const [, , sigil, query] = m;
      const q = query!.toLowerCase();
      const start = caret - query!.length - 1;
      const state = useStore.getState();
      let out: Suggestion[] = [];
      if (sigil === '@') {
        const users = mentionableUsers(state, channelId)
          .filter((u) => !u.deleted && (u.username.startsWith(q) || u.displayName.toLowerCase().includes(q) || displayNameIn(state, u.id, spaceId).toLowerCase().includes(q)))
          .slice(0, 8)
          .map<Suggestion>((u) => ({ key: `u${u.id}`, label: displayNameIn(state, u.id, spaceId), detail: `@${u.username}`, insert: `@${u.username} `, icon: 'user', userId: u.id }));
        const roles = spaceId
          ? (state.spaces[spaceId]?.roles ?? [])
              .filter((r) => !r.isDefault && r.name.toLowerCase().startsWith(q) && (r.mentionable || can(perms, Permission.MENTION_EVERYONE)))
              .slice(0, 5)
              .map<Suggestion>((r) => ({ key: `r${r.id}`, label: `@${r.name}`, detail: 'Role', insert: `@${r.name} `, icon: 'role', color: r.color }))
          : [];
        const special =
          spaceId && can(perms, Permission.MENTION_EVERYONE)
            ? (['everyone', 'here'] as const)
                .filter((x) => x.startsWith(q))
                .map<Suggestion>((x) => ({ key: x, label: `@${x}`, detail: x === 'everyone' ? 'Notify everyone who can see this channel' : 'Notify everyone online', insert: `@${x} `, icon: 'everyone' }))
            : [];
        out = [...users, ...roles, ...special];
      } else if (sigil === '#' && spaceId) {
        out = Object.values(state.channels)
          .filter((c) => c.spaceId === spaceId && c.type !== 'category' && c.type !== 'thread' && c.name.toLowerCase().includes(q))
          .slice(0, 8)
          .map((c) => ({ key: c.id, label: c.name, detail: c.type === 'voice' ? 'Voice' : undefined, insert: `#${c.name} `, icon: 'channel' }));
      } else if (sigil === ':' && q.length >= 2) {
        out = searchEmoji(q, 8).map((e) => ({ key: e.n, label: `:${e.n}:`, insert: e.e, icon: 'emoji', emoji: e.e }));
      }
      setSuggestions(out);
      setSelected(0);
      setTrigger(out.length ? { start, end: caret } : null);
    },
    [channelId, spaceId, perms, chatCommands, activeCommandId],
  );

  // The space's commands arrive a moment after the first "/": show them without waiting for another key.
  useEffect(() => {
    const el = area.current;
    if (commands && el && document.activeElement === el && el.value.startsWith('/')) computeSuggestions(el.value, el.selectionStart);
  }, [commands, computeSuggestions]);

  // An option whose values the bot suggests: ask it a moment after the last key, and only show the latest answer.
  const suggestionRound = useRef(0);
  useEffect(() => {
    const round = ++suggestionRound.current;
    const el = area.current;
    if (!command || !step?.current?.autocomplete || typed?.args == null || !el || el.selectionStart !== text.length || document.activeElement !== el) return;
    const option = step.current;
    const value = step.typed != null ? String(step.typed) : '';
    const others = Object.fromEntries(Object.entries(step.values ?? {}).filter(([name]) => name !== option.name));
    const timer = setTimeout(() => {
      void requestSuggestions(channelId, command, step.path, option.name, value, others).then((choices) => {
        if (round !== suggestionRound.current) return;
        const out = choices.map<Suggestion>((c) => ({
          key: `auto${String(c.value)}`,
          label: c.name,
          detail: String(c.value) === c.name ? undefined : String(c.value),
          insert: /\s/.test(String(c.value)) && !String(c.value).includes('"') ? `"${String(c.value)}" ` : `${String(c.value)} `,
          icon: 'choice',
        }));
        setSuggestions(out);
        setSelected(0);
        setTrigger(out.length ? { start: text.length - value.length, end: text.length } : null);
      });
    }, 200);
    return () => clearTimeout(timer);
    // `step` and `typed` are rebuilt from `text` and `command` on every render; keying on those keeps one request per pause.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, command, step?.current?.name, channelId]);

  function applySuggestion(s: Suggestion) {
    if (!trigger) return;
    if (s.commandId) setActiveCommandId(s.commandId);
    const next = text.slice(0, trigger.start) + s.insert + text.slice(trigger.end);
    setText(next);
    setSuggestions([]);
    setTrigger(null);
    const caret = trigger.start + s.insert.length;
    requestAnimationFrame(() => {
      area.current?.focus();
      area.current?.setSelectionRange(caret, caret);
    });
  }

  // ---- Formatting ------------------------------------------------------------------

  function wrap(before: string, after = before) {
    const el = area.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const selectedText = text.slice(s, e) || 'text';
    const next = text.slice(0, s) + before + selectedText + after + text.slice(e);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + before.length, s + before.length + selectedText.length);
    });
  }

  /** Starts the current line with a block marker: "> " quote, "# " heading or "- " list item. */
  function prefixLine(prefix: string) {
    const el = area.current;
    if (!el) return;
    const s = text.lastIndexOf('\n', el.selectionStart - 1) + 1;
    setText(text.slice(0, s) + prefix + text.slice(s));
    requestAnimationFrame(() => el.focus());
  }

  function insertAtCaret(value: string) {
    const el = area.current;
    const s = el?.selectionStart ?? text.length;
    const e = el?.selectionEnd ?? text.length;
    setText(text.slice(0, s) + value + text.slice(e));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(s + value.length, s + value.length);
    });
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (suggestions.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applySuggestion(suggestions[selected]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSuggestions([]);
        return;
      }
    }
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      wrap('**');
    } else if (mod && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      wrap('*');
    } else if (mod && e.key.toLowerCase() === 'e') {
      e.preventDefault();
      setEmojiOpen(true);
    } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    } else if (e.key === 'Escape' && replyToId) {
      e.preventDefault();
      ui.setReply(channelId, undefined);
    } else if (e.key === 'ArrowUp' && !text) {
      // Edit the last message I sent here.
      const state = useStore.getState();
      const l = state.messages[channelId];
      const mine = l?.ids
        .slice()
        .reverse()
        .map((id) => l.byId[id]!)
        .find((m) => m.author?.id === state.me?.id && !m.id.startsWith('pending:') && (m.type === 'default' || m.type === 'reply'));
      if (mine) {
        e.preventDefault();
        ui.setEditing(mine.id);
      }
    }
  }

  function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const files = Array.from(e.clipboardData.files);
    if (files.length) {
      e.preventDefault();
      void addFiles(files);
    }
  }

  const name = useMemo(() => {
    if (!channel) return '';
    if (channel.type === 'dm' || channel.type === 'group_dm') return privateChannelName(useStore.getState(), channel);
    return channel.type === 'thread' ? channel.name : `#${channel.name}`;
  }, [channel]);

  if (!channel) return null;

  if (!canSend) {
    return (
      <div className="px-4 pb-6 pt-1">
        <div className="rounded-lg bg-active px-4 py-3 text-sm text-muted">
          {systemDm
            ? 'This is an official Spawnwire account. It sends you updates, like Discover reviews, and cannot receive replies.'
            : channel.archived
            ? 'This thread is archived. Unarchive it to keep the conversation going.'
            : timedOut
              ? `You are timed out in this space. You can talk again in ${relativeFuture(myMember!.timeoutUntil!)}.`
              : 'You do not have permission to send messages in this channel.'}
        </div>
      </div>
    );
  }

  const remaining = LIMITS.messageLength - text.length;

  return (
    <div className="relative px-4 pb-6 pt-1">
      <InteractionNotices channelId={channelId} spaceId={spaceId} />
      {suggestions.length > 0 && (
        <div className="absolute bottom-full left-4 right-4 z-20 mb-1 overflow-hidden rounded-lg border border-line bg-overlay shadow-pop" role="listbox" aria-label="Suggestions">
          {suggestions.map((s, i) => (
            <button
              key={s.key}
              type="button"
              role="option"
              aria-selected={i === selected}
              onMouseEnter={() => setSelected(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                applySuggestion(s);
              }}
              className={clsx('flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm', i === selected ? 'bg-active' : '')}
            >
              {s.icon === 'user' && <Avatar user={useStore.getState().users[s.userId!]} size={22} />}
              {s.icon === 'command' && <Avatar user={s.bot} size={22} />}
              {s.icon === 'choice' && <ListChecks className="size-4 text-muted" aria-hidden />}
              {s.icon === 'role' && <AtSign className="size-4" style={s.color ? { color: s.color } : undefined} aria-hidden />}
              {s.icon === 'everyone' && <AtSign className="size-4 text-accent" aria-hidden />}
              {s.icon === 'channel' && <Hash className="size-4 text-muted" aria-hidden />}
              {s.icon === 'emoji' && <span className="w-[22px] text-center text-lg leading-none">{s.emoji}</span>}
              <span className={clsx('shrink-0 font-medium', s.icon === 'command' ? 'text-accent' : 'text-fg')}>{s.label}</span>
              {s.icon === 'user' && <UserBadge user={useStore.getState().users[s.userId!]} className="ml-0" />}
              {s.detail && <span className="truncate text-xs text-faint">{s.detail}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-lg bg-input ring-1 ring-line">
        {typed && typed.args !== null && (command || sameName.length > 1) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-t-lg border-b border-line bg-sidebar px-3 py-1.5 text-xs text-muted" aria-live="polite">
            {command ? (
              <>
                <span className="flex items-center gap-1.5">
                  <Avatar user={command.bot} size={16} />
                  <span className="font-mono font-semibold text-accent">/{command.name}</span>
                </span>
                {step?.path.map((p) => (
                  <span key={p} className="font-mono font-semibold text-accent">
                    {p}
                  </span>
                ))}
                {step?.next ? (
                  <span className="font-mono text-faint">{`<${step.next.map((n) => n.name).join(' | ')}>`}</span>
                ) : (
                  (step?.options ?? []).map((o) => (
                    <span key={o.name} className={clsx('font-mono', o.name === currentOption?.name ? 'rounded bg-accent-soft px-1 text-fg' : 'text-faint')}>
                      {o.required ? `<${o.name}>` : `[${o.name}]`}
                    </span>
                  ))
                )}
                <span className="w-full truncate">
                  {step?.next
                    ? 'Pick a subcommand'
                    : currentOption
                      ? `${currentOption.name}: ${currentOption.description}${currentOption.required ? '' : ' (optional)'}${currentOption.autocomplete ? ' · suggestions from the bot' : ''}`
                      : command.description}
                  {' · '}Enter to run, answered by {command.bot.displayName}
                </span>
              </>
            ) : (
              <span>Several bots have /{typed.name}. Type / again and pick the one you mean.</span>
            )}
          </div>
        )}
        {replyMessage && (
          <div className="flex items-center justify-between gap-2 rounded-t-lg border-b border-line bg-sidebar px-3 py-1.5 text-xs text-muted">
            <span className="truncate">
              Replying to <span className="font-semibold text-fg">{replyMessage.author ? displayNameIn(useStore.getState(), replyMessage.author.id, spaceId) : 'Deleted User'}</span>
            </span>
            <button type="button" aria-label="Cancel reply" onClick={() => ui.setReply(channelId, undefined)} className="rounded p-0.5 hover:bg-hover hover:text-fg">
              <X className="size-4" />
            </button>
          </div>
        )}

        {uploads.length > 0 && (
          <ul className="flex gap-2 overflow-x-auto border-b border-line p-3 scroll-thin" aria-label="Attachments">
            {uploads.map((u) => (
              <li key={u.key} className="relative w-40 shrink-0 rounded-md border border-line bg-sidebar p-2">
                <div className="flex h-24 items-center justify-center overflow-hidden rounded bg-active">
                  {u.preview ? <img src={u.preview} alt="" className="h-full w-full object-cover" /> : <FileIcon className="size-10 text-muted" aria-hidden />}
                </div>
                <div className="mt-1 truncate text-xs text-fg" title={u.file.name}>
                  {u.file.name}
                </div>
                {u.error ? (
                  <div className="text-[11px] text-danger">{u.error}</div>
                ) : u.attachment ? (
                  <div className="text-[11px] text-faint">{formatBytes(u.file.size)}</div>
                ) : (
                  <div className="mt-1 h-1.5 overflow-hidden rounded bg-active" role="progressbar" aria-valuenow={Math.round(u.progress * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`Uploading ${u.file.name}`}>
                    <div className="h-full bg-accent transition-[width]" style={{ width: `${Math.round(u.progress * 100)}%` }} />
                  </div>
                )}
                <button type="button" aria-label={`Remove ${u.file.name}`} onClick={() => removeUpload(u)} className="absolute right-1 top-1 rounded bg-overlay p-0.5 text-muted shadow hover:text-danger">
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {showFormatting && (
          <div className="flex items-center gap-0.5 border-b border-line px-2 py-1">
            <IconButton icon={Bold} label="Bold (Ctrl B)" size="sm" onClick={() => wrap('**')} />
            <IconButton icon={Italic} label="Italic (Ctrl I)" size="sm" onClick={() => wrap('*')} />
            <IconButton icon={Strikethrough} label="Strikethrough" size="sm" onClick={() => wrap('~~')} />
            <IconButton icon={Code} label="Code" size="sm" onClick={() => wrap('`')} />
            <IconButton icon={EyeOff} label="Spoiler" size="sm" onClick={() => wrap('||')} />
            <IconButton icon={Quote} label="Quote" size="sm" onClick={() => prefixLine('> ')} />
            <IconButton icon={Heading} label="Heading" size="sm" onClick={() => prefixLine('# ')} />
            <IconButton icon={List} label="Bulleted list" size="sm" onClick={() => prefixLine('- ')} />
          </div>
        )}

        <div className="flex items-end gap-1 px-2 py-1.5">
          {canAttach && (
            <>
              <IconButton icon={Paperclip} label="Attach files" onClick={() => fileInput.current?.click()} />
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  void addFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
            </>
          )}
          <textarea
            ref={area}
            data-composer={channelId}
            value={text}
            rows={1}
            maxLength={LIMITS.messageLength + 200}
            placeholder={placeholder ?? `Message ${name}`}
            aria-label={`Message ${name}`}
            className="max-h-80 min-h-[36px] flex-1 resize-none bg-transparent px-1 py-2 text-[15px] leading-5 text-fg placeholder:text-faint outline-none"
            onChange={(e) => {
              setText(e.target.value);
              computeSuggestions(e.target.value, e.target.selectionStart);
              if (e.target.value.trim()) sendTyping(channelId);
            }}
            onClick={(e) => computeSuggestions(text, e.currentTarget.selectionStart)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onBlur={() => setTimeout(() => setSuggestions([]), 150)}
          />
          <IconButton icon={Type} label={showFormatting ? 'Hide formatting' : 'Show formatting'} active={showFormatting} onClick={() => setShowFormatting((v) => !v)} />
          <EmojiPicker open={emojiOpen} onOpenChange={setEmojiOpen} onPick={(e) => insertAtCaret(e)}>
            <IconButton icon={Smile} label="Emoji (Ctrl E)" />
          </EmojiPicker>
          <IconButton icon={SendHorizontal} label={cooling ? `Slow mode: wait ${secondsLeft}s` : 'Send'} disabled={cooling || uploading || (!text.trim() && ready.length === 0)} onClick={() => void submit()} className="text-accent" />
        </div>
      </div>
      {(remaining < 200 || slowmodeOn) && (
        <div className="mt-1 flex items-center justify-end gap-3 text-xs">
          {slowmodeOn && (
            <span className={clsx('flex items-center gap-1', cooling ? 'text-warning' : 'text-faint')} aria-live="polite">
              <Timer className="size-3.5" aria-hidden />
              {cooling ? `Slow mode: you can send again in ${secondsLeft}s` : `Slow mode is on (${slowmode < 60 ? `${slowmode}s` : `${Math.round(slowmode / 60)}m`})`}
            </span>
          )}
          {remaining < 200 && <span className={remaining < 0 ? 'text-danger' : 'text-faint'}>{remaining}</span>}
        </div>
      )}
    </div>
  );
});
